// src/components/.../LeafNode.js
import React, { useContext, useState, useRef, useMemo, useEffect } from 'react';
import { FaArrowUp, FaArrowDown, FaTrash, FaQuestionCircle, FaChevronDown } from 'react-icons/fa';
import AppContext from '../../contexts/AppContext';
import ConfirmModal from '../utils/ConfirmModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import MapComponent from '../commons/MapComponent';
import ImageUploader from '../utils/ImageUploader';
import { UploadOutlined } from '@ant-design/icons';
import IconPicker from './IconPicker';
import { buildQuillModules, QUILL_TEXT_FORMATS } from '../utils/quillConfig';

/**
 * 视图模式
 * - normal：正常模式（不显示 Embedded / External 两个区块）
 * - pdf：Embedded Document（PDF 上传）
 * - external：External Url（文本域输入 URL）
 */
const MODE = {
  NORMAL: 'normal',
  PDF: 'pdf',
  EXTERNAL: 'external',
};

const LeafNode = ({ detail, setDetail, handleInputChange, canSwitch, handleImageDelete }) => {
  const { selectedRole, token, userInfo } = useContext(AppContext);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [switchState, setSwitchState] = useState(detail.isLeaf);
  const GoogleMapApiKey = 'AIzaSyD63grvHYISPK4dLfyj5kKmC6MR_wQPJ80';
  const reactQuillRef = useRef();
  const API = process.env.REACT_APP_API_BASE_URL;


  const prefix = userInfo?.user?.S3Id ? `full/${userInfo.user.S3Id}/` : undefined;
  const thumbPrefix = userInfo?.user?.S3Id ? `thumb/${userInfo.user.S3Id}/` : undefined;

  /** ---------- 模式初始值来自 externalUrl.name ---------- */
  const initialMode = useMemo(() => {
    if (detail?.externalUrl?.name === 'PDF') return MODE.PDF;
    if (detail?.externalUrl?.name === 'External') return MODE.EXTERNAL;
    return MODE.NORMAL;
  }, [detail?.externalUrl]);

  const [viewMode, setViewMode] = useState(initialMode);
  const fieldLabelClass = "block text-[0.78rem] font-semibold uppercase tracking-[0.16em] text-[#7f90b3]";
  const textInputClass = "mt-1 block w-full rounded-[18px] border border-[#dbe3f5] bg-white px-4 py-3 text-[14px] text-[#24324d] shadow-none outline-none transition-all duration-200 placeholder:text-[#99a4bc] hover:border-[#c8d3fb] hover:bg-[#f8faff] focus:border-[#aebcf1] focus:bg-white focus:ring-4 focus:ring-[rgba(74,92,255,0.08)]";
  const sectionTitleClass = "block text-sm font-semibold text-[#334155]";
  const ghostActionClass = "inline-flex items-center gap-2 rounded-xl border border-[#d9e3ff] bg-white px-4 py-2 text-sm font-medium text-[#4a5cff] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#c8d3fb] hover:bg-[#eef2ff] hover:shadow-[0_10px_20px_rgba(74,92,255,0.12)]";

  /** ---------- External Url 本地输入 & 校验 ---------- */
  const [externalInput, setExternalInput] = useState(detail?.externalUrl?.name === 'External' ? (detail?.externalUrl?.url || '') : '');
  const [externalError, setExternalError] = useState('');

  useEffect(() => {
    setViewMode(initialMode);
    setExternalInput(
      detail?.externalUrl?.name === 'External' ? (detail?.externalUrl?.url || '') : ''
    );
    setExternalError('');
  }, [detail?.id, detail?.externalUrl?.name, detail?.externalUrl?.url, initialMode]);

  const urlIsValid = (url) => {
    try {
      const u = new URL(url);
      return u.protocol === 'http:' || u.protocol === 'https:';
    } catch {
      return false;
    }
  };

  const applyExternalUrl = () => {
    if (!externalInput || !urlIsValid(externalInput)) {
      setExternalError('Please input a valid http(s) URL.');
      return;
    }
    setExternalError('');
    setDetail((prev) => ({
      ...prev,
      externalUrl: { name: 'External', url: externalInput.trim() },
    }));
  };

  /** ---------- 联系方式 & 图标 ---------- */
  const contactOptions = [
    { label: 'Email', value: 'email' },
    { label: 'Phone', value: 'phone' },
    { label: 'Facebook', value: 'facebook' },
    { label: 'Instagram', value: 'instagram' },
    { label: 'WhatsApp', value: 'whatsapp' },
    { label: 'Website', value: 'website' },
  ];

  const handleContactCheckboxChange = (contactType) => {
    setDetail((prevDetail) => {
      const newContact = { ...(prevDetail.contact || {}) };
      const keys = Object.keys(newContact);
      if (keys.includes(contactType)) {
        delete newContact[contactType];
      } else {
        if (keys.length < 3) {
          newContact[contactType] = '';
        }
      }
      return { ...prevDetail, contact: newContact };
    });
  };

  const handleContactValueChange = (contactType, newValue) => {
    setDetail((prevDetail) => {
      const newContact = { ...(prevDetail.contact || {}) };
      if (Object.keys(newContact).includes(contactType)) {
        newContact[contactType] = newValue;
      }
      return { ...prevDetail, contact: newContact };
    });
  };

  const handleSwitch = () => setIsModalOpen(true);

  const confirmSwitch = () => {
    setIsModalOpen(false);
    setSwitchState(!switchState);
    setDetail((prevDetail) => ({
      ...prevDetail,
      isLeaf: false,
      description: '',
      bannerImage: '',
      iconImage: '',
      carouselImages: [],
      mainText: '',
      // 不强制清空 externalUrl，按你的需求仅切换到目录模式
    }));
  };

  const carouselImageUp = (index, name) => {
    if (index > 0) {
      setDetail((prevDetail) => {
        const arr = [...prevDetail[name]];
        [arr[index - 1], arr[index]] = [arr[index], arr[index - 1]];
        return { ...prevDetail, [name]: arr };
      });
    }
  };

  const carouselImageDown = (index, name) => {
    setDetail((prevDetail) => {
      const arr = [...prevDetail[name]];
      if (index < arr.length - 1) {
        [arr[index], arr[index + 1]] = [arr[index + 1], arr[index]];
      }
      return { ...prevDetail, [name]: arr };
    });
  };

  const removeCarouselImage = (index, name) => {
    setDetail((prevDetail) => {
      const arr = prevDetail[name].filter((_, i) => i !== index);
      return { ...prevDetail, [name]: arr };
    });
  };

  const looksLikeWebHost = (host) => {
    const normalizedHost = String(host || '').trim().toLowerCase();
    if (!normalizedHost) return false;

    if (normalizedHost === 'localhost') return true;
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(normalizedHost)) return true;

    const labels = normalizedHost.split('.');
    if (labels.length < 2) return false;

    const tld = labels[labels.length - 1];
    if (!/^[a-z]{2,63}$/.test(tld)) return false;

    return labels.every((label) => /^[a-z0-9-]+$/i.test(label) && !label.startsWith('-') && !label.endsWith('-'));
  };
  const normalizeSafeHref = (rawHref) => {
    const href = String(rawHref || '').trim();
    if (!href) return null;

    const hasScheme = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(href);
    const looksLikeExplicitWebAddress =
      href.includes('.') || href.startsWith('localhost') || href.startsWith('localhost:');
    if (!hasScheme && !looksLikeExplicitWebAddress) return null;
    const normalizedHref = hasScheme ? href : `https://${href}`;

    try {
      const parsed = new URL(normalizedHref);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;
      if (!looksLikeWebHost(parsed.hostname)) return null;
      return parsed.toString();
    } catch (error) {
      return null;
    }
  };

  // 链接过滤函数，只允许包含 platypus360 的链接，其他全部去掉超链接，只保留文字
  const sanitizeLinks = (html) => {
    if (typeof window === 'undefined') return html;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const anchors = doc.querySelectorAll('a');
      anchors.forEach(a => {
        const href = a.getAttribute('href') || '';

        // 只允许包含 platypus360 的链接，其余全部去掉超链接，只保留文字
        if (!href.includes('platypus360')) {
          const span = doc.createElement('span');
          // 保留原来 a 里面的内容
          span.innerHTML = a.innerHTML;
          a.parentNode.replaceChild(span, a);
        }
      });

      return doc.body.innerHTML;
    } catch (e) {
      console.error('sanitizeLinks error:', e);
      return html;
    }
  };
  // Legacy fallback kept temporarily to avoid risky cleanup in this mixed-encoding file.
  void sanitizeLinks;

  const sanitizeExternalLinks = (html) => {
    if (typeof window === 'undefined') return html;

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      doc.querySelectorAll('a').forEach((anchor) => {
        const safeHref = normalizeSafeHref(anchor.getAttribute('href'));
        if (!safeHref) {
          const span = doc.createElement('span');
          span.innerHTML = anchor.innerHTML;
          anchor.parentNode?.replaceChild(span, anchor);
          return;
        }

        anchor.setAttribute('href', safeHref);
        if (/^https?:/i.test(safeHref)) {
          anchor.setAttribute('target', '_blank');
          anchor.setAttribute('rel', 'noopener noreferrer');
        } else {
          anchor.removeAttribute('target');
          anchor.removeAttribute('rel');
        }
      });

      return doc.body.innerHTML;
    } catch (error) {
      console.error('sanitizeExternalLinks error:', error);
      return html;
    }
  };

  const handleEditorChange = (newContent) => {
    const sanitized = sanitizeExternalLinks(newContent);

    if (sanitized.length < 4000) {
      setDetail((prevDetail) => ({ ...prevDetail, mainText: sanitized }));
    }
  };

  const quillModules = buildQuillModules([
    [{ size: ['small', false, 'large', 'huge'] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['bold', 'italic', 'underline', 'strike', 'link'],
    [{ color: [] }, { background: [] }],
    ['clean'],
  ]);

  const quillFormats = [
    ...QUILL_TEXT_FORMATS,
    'size',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent', 'align',
    'color', 'background', 'link',
  ];

  const handleInputNestedChange = (e, arrayName, index, nestedKey) => {
    const { name, value } = e.target;
    setDetail((prevDetail) => {
      if (arrayName) {
        return {
          ...prevDetail,
          [arrayName]: prevDetail[arrayName].map((item, i) => (i === index ? value : item)),
        };
      } else if (nestedKey) {
        return {
          ...prevDetail,
          [nestedKey]: {
            ...prevDetail[nestedKey],
            [name]: value,
          },
        };
      } else {
        return { ...prevDetail, [name]: value };
      }
    });
  };

  const handleLocationSelect = (location) => {
    setDetail((prevDetail) => ({
      ...prevDetail,
      geoLocation: {
        ...(prevDetail.geoLocation || {}),
        latitude: location.latitude,
        longitude: location.longitude,
      },
    }));
  };

  const checkCharacterCount = (event) => {
    if (!reactQuillRef.current) return;
    const unprivilegedEditor = reactQuillRef.current.getEditor();
    const textOnly = unprivilegedEditor.getText().trim();
    if (textOnly.length >= 4000 && event.key !== 'Backspace') {
      event.preventDefault();
    }
  };

  /** ---------------- PDF 上传（仅 PDF） => externalUrl:{name:'PDF',url} ---------------- */
  const onPdfSelect = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Only PDF files are allowed.');
      e.target.value = '';
      return;
    }
    if (file.size > 20 * 1024 * 1024) {
      alert('File too large. Max 20 MB.');
      e.target.value = '';
      return;
    }

    try {
      const formData = new FormData();
      formData.append('pdf', file);

      const resp = await fetch(`${API}/private/pdf/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || resp.statusText || 'Upload failed');
      }

      const data = await resp.json();
      const url = data.fileUrl;
      if (!url) throw new Error('fileUrl missing in response');

      setDetail((prev) => ({ ...prev, externalUrl: { name: 'PDF', url } }));
      setViewMode(MODE.PDF);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error uploading PDF');
    } finally {
      e.target.value = '';
    }
  };

  const clearEmbeddedDoc = () => {
    // 清空 PDF 仅清除 externalUrl
    if (detail?.externalUrl?.name === 'PDF') {
      setDetail((prev) => ({ ...prev, externalUrl: undefined }));
    }
  };

  /** ---------- 显示条件 ---------- */
  const showNormalBlocks = viewMode === MODE.NORMAL;
  const showPdfBlock = viewMode === MODE.PDF;
  const showExternalBlock = viewMode === MODE.EXTERNAL;

  return (
    <div>
      {/* 顶部目录切换按钮 */}
      <div className={`flex items-center justify-between ${canSwitch ? '' : 'hidden'}`}>
        <button
          onClick={handleSwitch}
          className="inline-flex items-center rounded-xl border border-[#ffd9e0] bg-[#fff5f7] px-4 py-2 text-sm font-medium text-[#ff4d6d] transition-all duration-200 hover:-translate-y-[1px] hover:border-[#ffc5cf] hover:bg-[#fff0f3] hover:shadow-[0_10px_20px_rgba(255,77,109,0.12)]"
        >
          Switch to Directory List?
        </button>
      </div>

      <ConfirmModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onConfirm={confirmSwitch}
        header="Do you want to proceed?"
        content="Switching will clear all data and change to Index mode."
      />

      {/* 视图模式选择 */}
      <div className="mt-4 rounded-[22px] border border-[#e7ebf8] bg-[linear-gradient(180deg,#ffffff_0%,#fbfcff_100%)] px-5 py-4 shadow-[0_12px_26px_rgba(30,52,120,0.05)] transition-all duration-200 hover:border-[#d4ddf8] hover:shadow-[0_16px_30px_rgba(30,52,120,0.07)]">
        <label className="inline-flex items-center gap-2 text-sm font-semibold text-[#334155]">
          Display Mode
          {/* Info icon + tooltip */}
          <span className="relative group">
            <span
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#eef2ff] text-[#4663ff] text-xs font-bold select-none"
              aria-label="Display mode help"
            >
              ?
            </span>
            {/* Tooltip */}
            <div
              role="tooltip"
              className="absolute z-50 hidden group-hover:block left-1/2 -translate-x-1/2 mt-2 w-64 rounded-[18px] border border-[#dbe3f5] bg-white p-3 text-xs text-[#52607d] shadow-[0_12px_24px_rgba(15,23,42,0.08)]"
            >
              <div className="font-semibold mb-1">Mode Explanation</div>
              <ul className="list-disc pl-4 space-y-1">
                <li><span className="font-medium">Standard</span>: Normal display (no external resource).</li>
                <li><span className="font-medium">Enhanced</span>: <i>Embedded Document</i> (e.g. PDF).</li>
                <li><span className="font-medium">Dynamic</span>: <i>External URL Link</i>.</li>
              </ul>
            </div>
          </span>
        </label>

        <div className="relative ml-3 inline-flex items-center">
          <select
            value={viewMode}
            onChange={(e) => {
              setViewMode(e.target.value);
              setDetail((prev) => ({ ...prev, externalUrl: null }));
            }}
            className="appearance-none rounded-xl border border-[#dbe3f5] bg-white py-2 pl-3 pr-10 text-sm font-medium text-[#24324d] outline-none transition-all duration-200 hover:border-[#c8d3fb] hover:bg-[#f8faff] focus:border-[#aebcf1] focus:ring-4 focus:ring-[rgba(74,92,255,0.08)]"
          >
            <option value={MODE.NORMAL}>Standard</option>
            <option value={MODE.PDF}>Enhanced</option>
            <option value={MODE.EXTERNAL}>Dynamic</option>
          </select>
          <span className="pointer-events-none absolute right-3 inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#eef2ff] text-[#4a63ff] transition-all duration-200">
            <FaChevronDown className="h-3 w-3" />
          </span>
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:space-x-4">
        {/* ---------- 左侧 ---------- */}
        <div className="w-full md:w-1/2 p-1">
          {/* Name：始终显示 */}
          <label className="block mt-4">
            <span className={fieldLabelClass}>
              Name
              <span className="text-xs"> &nbsp;{detail.name ? detail.name.length : 0}/50</span>
            </span>
            <input
              type="text"
              name="name"
              value={detail.name || ''}
              onChange={handleInputChange}
              maxLength={50}
              className={textInputClass}
            />
          </label>

          {/* Content Body：仅 Normal 显示 */}
          {showNormalBlocks && (
            <label className="block mt-4">
              <span className={fieldLabelClass}>
                Content Body
                <span className="text-xs">
                  &nbsp;{detail.mainText ? detail.mainText.length : 0}/4000
                </span>
              </span>
              <div
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="overflow-hidden rounded-[18px] border border-[#dbe3f5] bg-white shadow-[0_10px_22px_rgba(15,23,42,0.04)] transition-all duration-200 hover:border-[#c8d3fb]">
                  <ReactQuill
                    ref={reactQuillRef}
                    value={detail.mainText || ''}
                    onChange={handleEditorChange}
                    modules={quillModules}
                    formats={quillFormats}
                    className="bg-white"
                    onKeyDown={checkCharacterCount}
                  />
                </div>
              </div>
            </label>
          )}

          {/* Display Icon：始终显示 */}
          <div className='w-full p-1'>

            <IconPicker
              value={detail.iconImage || null}
              onChange={(url) => {
                if (!url) {
                  setDetail(prev => ({ ...prev, iconImage: '', brandImagePrivate: '' }));
                  return;
                }
                const file = url.split('/').pop() || '';
                setDetail(prev => ({
                  ...prev,
                  iconImage: url,            // 展示用完整 URL
                  brandImagePrivate: file    // 入库/回传用文件名
                }));
              }}
              className="mt-2"
            />
          </div>

          {/* GeoLocation：仅 Normal 且不是 venue 显示 */}
          {showNormalBlocks && detail && detail.geoLocation && selectedRole && selectedRole.type !== 'venue' && (
            <div>
              <label className="block mt-2">
                <span className={fieldLabelClass}>
                  GeoLocation Name
                  <span className="text-xs">
                    &nbsp;{detail.geoLocation.name ? detail.geoLocation.name.length : 0}/50
                  </span>
                </span>
                <input
                  type="text"
                  name="name"
                  value={detail.geoLocation.name || ''}
                  onChange={(e) => handleInputNestedChange(e, null, null, 'geoLocation')}
                  maxLength={50}
                  className={textInputClass}
                />
              </label>

              <label className="block mt-4">
                <span className={fieldLabelClass}>
                  GeoLocation Description
                  <span className="text-xs">
                    &nbsp;{detail.geoLocation.description ? detail.geoLocation.description.length : 0}/60
                  </span>
                </span>
                <textarea
                  name="description"
                  value={detail.geoLocation.description || ''}
                  onChange={(e) => handleInputNestedChange(e, null, null, 'geoLocation')}
                  maxLength={1500}
                  className={textInputClass}
                />
              </label>

              <label className="block mt-4">
                <span className={fieldLabelClass}>GeoLocation Type</span>
                <div className="relative mt-1">
                  <select
                    name="type"
                    value={detail.geoLocation.type || ''}
                    onChange={(e) => handleInputNestedChange(e, null, null, 'geoLocation')}
                    className={`${textInputClass} mt-0 appearance-none pr-12`}
                  >
                    <option value="">Please select</option>
                    <option value="adventure_activities">Adventure Activities</option>
                    <option value="bank">Bank</option>
                    <option value="entertainment">Entertainment</option>
                    <option value="home">Home</option>
                    <option value="other">Other</option>
                    <option value="parks_and_nature">Parks and Nature</option>
                    <option value="restaurant">Restaurant</option>
                    <option value="shop">Shop</option>
                    <option value="souvenirs">Souvenirs</option>
                    <option value="transport">Transport</option>
                  </select>
                  <span className="pointer-events-none absolute right-3 top-1/2 inline-flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-[#eef2ff] text-[#4a63ff] transition-all duration-200">
                    <FaChevronDown className="h-3 w-3" />
                  </span>
                </div>
              </label>

              <div>
                <h1 className="mt-4 text-sm font-semibold text-[#334155]">Click the map to set location</h1>
                <MapComponent
                  apiKey={GoogleMapApiKey}
                  initialGeolocation={{
                    latitude: detail.geoLocation.latitude || -37.81485579713169,
                    longitude: detail.geoLocation.longitude || 144.95951682329178,
                    defaultZoom: 12,
                  }}
                  onLocationSelect={handleLocationSelect}
                />
              </div>
            </div>
          )}
        </div>

        {/* ---------- 右侧 ---------- */}
        <div className="w-full md:w-1/2 p-1">
          {/* Embedded Document：仅 Embedded 模式显示 */}
          {showPdfBlock && (
            <div className="block mt-2">
              <span className={sectionTitleClass}>
                Embedded Document
                <span className="inline-flex items-center ml-2 relative group cursor-help align-middle">
                  <FaQuestionCircle className="w-4 h-4 text-gray-400" />
                  <span className="absolute left-1/2 -translate-x-1/2 top-full mt-2 hidden group-hover:block z-20 whitespace-nowrap rounded px-2 py-1 text-xs text-white bg-black/80 shadow">
                    Only PDF files (&lt;20MB) are supported.
                  </span>
                </span>
              </span>
              <div className="mt-1 flex items-center gap-3">
                <label className={`${ghostActionClass} mt-1 cursor-pointer`}>
                  <UploadOutlined />
                  <input type="file" accept="application/pdf" onChange={onPdfSelect} className="hidden" />
                  Upload File
                </label>
                {detail?.externalUrl?.name === 'PDF' && detail.externalUrl.url && (
                  <>
                    <a
                      href={detail.externalUrl.url}
                      target="_blank"
                      rel="noreferrer"
                      className="break-all text-sm font-medium text-[#2557d6] hover:underline"
                    >
                      Open current Document
                    </a>
                    <FaTrash className="h-5 w-5 text-red-500 cursor-pointer" onClick={clearEmbeddedDoc} />
                  </>
                )}
              </div>
            </div>
          )}

          {/* External Url：仅 External 模式显示 */}
          {showExternalBlock && (
            <div className="block mt-2">
              <span className={sectionTitleClass}>External Link</span>
              <textarea
                rows={3}
                className={textInputClass}
                placeholder="https://example.com/path"
                value={externalInput}
                onChange={(e) => {
                  setExternalInput(e.target.value);
                  if (externalError) setExternalError('');
                }}
                onBlur={() => {
                  if (externalInput && urlIsValid(externalInput)) {
                    applyExternalUrl();
                  }
                }}
              />
            </div>
          )}

          {/* Banner Image：三种模式都显示 */}
          <div className="block mt-2">
            <span className={sectionTitleClass}>Banner Image</span>
            <div className="flex items-center">
              <label className="mt-1 block w-full border-gray-300 items-center">
                <ImageUploader
                  token={token}
                  prefix={prefix}
                  thumbPrefix={thumbPrefix}
                  width={540}
                  height={540}
                  objectFit="cover"
                  buttonText="Edit Banner Image"
                  value={detail.bannerImage}
                  onChange={(url) => {
                    setDetail((prev) => ({ ...prev, bannerImage: url }));
                  }}
                />
              </label>
            </div>
          </div>

          {detail.bannerImage && (
            <div className="mt-3 flex w-full items-center justify-between gap-4 rounded-[20px] border border-[#e6ebf8] bg-[#f8faff] p-3">
              <div className="relative w-1/2 overflow-hidden rounded-[18px] border border-[#dbe3f5] bg-white shadow-[0_10px_20px_rgba(15,23,42,0.06)]">
                <img src={detail.bannerImage} alt="sideImage" className="block w-full" />
              </div>
              <FaTrash
                className="h-6 w-6 z-10 cursor-pointer text-red-500 transition-transform duration-200 hover:scale-110"
                onClick={() => handleImageDelete('bannerImage')}
              />
            </div>
          )}

          {/* 轮播图：仅 Normal 显示 */}
          {showNormalBlocks && (
            <div className="block mt-2">
              <span className={sectionTitleClass}>Carousel Images (Max 6)</span>
              <div className="flex items-center">
                <label className="mt-1 block w-full border-gray-300 items-center">
                  {detail?.carouselImages?.length < 6 && (
                    <ImageUploader
                      token={token}
                      prefix={prefix}
                      thumbPrefix={thumbPrefix}
                      width={960}
                      height={540}
                      objectFit="cover"
                      buttonText="Edit Carousel Image"
                      onChange={(url) => {
                        setDetail((prev) => ({
                          ...prev,
                          carouselImages: [...prev.carouselImages, url],
                        }));
                      }}
                    />
                  )}
                </label>
              </div>
              <div className="carousel-thumbnails mt-2">
                {detail.carouselImages &&
                  detail.carouselImages.map((url, index) => (
                    <div key={index} className="carousel-thumbnail mt-3 flex items-center justify-between gap-4 rounded-[20px] border border-[#e6ebf8] bg-[#f8faff] p-3">
                      <img src={url} alt={`Carousel ${index}`} className="w-2/3 overflow-hidden rounded-[18px] border border-[#dbe3f5] bg-white shadow-[0_10px_20px_rgba(15,23,42,0.06)]" />
                      <div className="flex flex-col md:flex-row items-center space-y-2 md:space-y-0 md:space-x-1">
                        <FaArrowUp
                          className="h-6 w-6 cursor-pointer text-gray-500 transition-transform duration-200 hover:scale-110 hover:text-[#2557d6]"
                          onClick={() => carouselImageUp(index, 'carouselImages')}
                        />
                        <FaArrowDown
                          className="h-6 w-6 cursor-pointer text-gray-500 transition-transform duration-200 hover:scale-110 hover:text-[#2557d6]"
                          onClick={() => carouselImageDown(index, 'carouselImages')}
                        />
                        <FaTrash
                          className="h-6 w-6 cursor-pointer text-red-500 transition-transform duration-200 hover:scale-110"
                          onClick={() => removeCarouselImage(index, 'carouselImages')}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* 联系方式：仅 Normal 且不是 venue 显示 */}
          {showNormalBlocks && selectedRole && selectedRole.type !== 'venue' && (
            <div className="block mt-2">
              <span className={sectionTitleClass}>Contacts (Maximum 3)</span>
              <div>
                {contactOptions.map((option) => {
                  const isSelected = detail.contact && Object.keys(detail.contact).includes(option.value);
                  return (
                    <label key={option.value} className="mt-2 flex items-center justify-between gap-3 rounded-[18px] border border-[#e6ebf8] bg-[#fbfcff] px-4 py-3">
                      <p>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleContactCheckboxChange(option.value)}
                        />
                        <span className="pl-2 text-sm text-[#334155]">{option.label}</span>
                      </p>
                      <input
                        type="text"
                        value={(detail.contact && detail.contact[option.value]) || ''}
                        onChange={(e) => handleContactValueChange(option.value, e.target.value)}
                        maxLength={80}
                        className={`${textInputClass} mt-0 max-w-[70%]`}
                        disabled={!isSelected}
                      />
                    </label>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeafNode;
